import React from 'react';
import { Grid, Link, Paper, Stack, Typography } from '@mui/material';
import type { ReportVisual } from '../../services/reportsApi';
import ReportEmptyState from './ReportEmptyState';

type ReportScorecardProps = {
  visual: ReportVisual;
  compact?: boolean;
};

function formatMetricValue(value: unknown): string {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '0';
  if (typeof value === 'string') return value;
  return '0';
}

const ReportScorecard: React.FC<ReportScorecardProps> = ({ visual, compact = false }) => {
  const items = Array.isArray(visual.data?.items) ? visual.data.items : [];

  return (
    <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2, height: '100%' }}>
      <Typography variant="h6">{visual.title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: compact ? 1 : 1.5 }}>
        {visual.insight}
      </Typography>

      {visual.empty_state ? (
        <ReportEmptyState
          title={visual.empty_state.title}
          detail={visual.empty_state.detail}
          missingData={visual.empty_state.missing_data}
          compact={compact}
        />
      ) : (
        <Grid container spacing={compact ? 1 : 1.5}>
          {items.map((item: Record<string, unknown>, index: number) => (
            <Grid item xs={6} sm={4} md={3} key={String(item?.id || item?.label || index)}>
              <Paper variant="outlined" sx={{ p: compact ? 1 : 1.25, height: '100%' }}>
                <Stack spacing={0.5}>
                  {typeof item?.href === 'string' && item.href ? (
                    <Link href={item.href} underline="hover" color="inherit">
                      <Typography variant="caption" color="text.secondary">
                        {String(item?.label || 'Metric')}
                      </Typography>
                    </Link>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {String(item?.label || 'Metric')}
                    </Typography>
                  )}
                  <Typography variant={compact ? 'h6' : 'h5'}>
                    {formatMetricValue(item?.value)}
                    {item?.suffix ? String(item.suffix) : ''}
                  </Typography>
                  {item?.detail ? (
                    <Typography variant="caption" color="text.secondary">
                      {String(item.detail)}
                    </Typography>
                  ) : null}
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Paper>
  );
};

export default ReportScorecard;
